"use client"

import { useConvexConnectionState, useQuery } from "convex/react"
import { CountUp } from "@/components/home/count-up"
import { ConvexUrlProvider } from "@/components/providers/convex-url-provider"
import { fmtPctRatio, fmtRivBurn, fmtRivCompact } from "@/lib/rivyu-metrics-format"
import {
  pickCurrentActiveUsers,
  pickProtocolPoolMetrics,
  rivyuConvexBugBountyStats,
  rivyuConvexEcosystemMetrics,
  rivyuConvexGlobalStats,
  rivyuConvexIdeaPadStats,
  rivyuConvexInvestorStats,
  rivyuConvexUtilityPoolStats,
  type RivyuBugBountyStatsPublic,
  type RivyuEcosystemMetricsPublic,
  type RivyuGlobalStatsPublic,
  type RivyuIdeaPadStatsPublic,
  type RivyuInvestorStatsPublic,
  type RivyuUtilityPoolStatsPublic,
} from "@/lib/rivyu-convex-queries"

function LiveConvexMeta({ updatedAt }: { updatedAt?: number }) {
  const state = useConvexConnectionState()
  const label = state.isWebSocketConnected ? "LIVE" : state.hasEverConnected ? "RECONNECTING" : "CONNECTING"
  const ts =
    updatedAt && updatedAt > 0
      ? new Date(updatedAt).toISOString().slice(11, 19) + "Z"
      : "—"
  return (
    <div className="tk-live-meta">
      <span>
        <span className="lit">◆</span> CONVEX
      </span>
      <span>{label}</span>
      <span>UPDATED {ts}</span>
    </div>
  )
}

function TokenomicsLiveMetricsInner() {
  const global = useQuery(rivyuConvexGlobalStats, {}) as RivyuGlobalStatsPublic | undefined
  const utility = useQuery(rivyuConvexUtilityPoolStats, {}) as RivyuUtilityPoolStatsPublic | undefined
  const eco = useQuery(rivyuConvexEcosystemMetrics, {}) as RivyuEcosystemMetricsPublic | undefined
  const investor = useQuery(rivyuConvexInvestorStats, {}) as RivyuInvestorStatsPublic | null | undefined
  const ideas = useQuery(rivyuConvexIdeaPadStats, {}) as RivyuIdeaPadStatsPublic | undefined
  const bugs = useQuery(rivyuConvexBugBountyStats, {}) as RivyuBugBountyStatsPublic | undefined
  const loading = global === undefined || eco === undefined
  const investorLoading = investor === undefined
  const investorReady = investor != null
  const ideasLoading = ideas === undefined
  const bugsLoading = bugs === undefined

  const pools = pickProtocolPoolMetrics(global, investor, utility)
  const { totalMined, miningPoolPaid, totalStaked, stakingPoolPaid, utilityPoolTotal, poolPct } = pools
  const uniqueStakers = global?.uniqueStakers ?? investor?.staking.uniqueStakers
  const totalBurned = eco?.totalBurned ?? investor?.chests.totalRivBurnedOnChain ?? 0
  const dau = investorReady ? investor.engagement.dau : pickCurrentActiveUsers(global)
  const ignored = global?.blockedUsers ?? investor?.users.ignoredUsers ?? 0
  const tractionUpdatedAt = investor?.cacheUpdatedAt ?? (investor?.snapshotAt ? Date.parse(investor.snapshotAt) : undefined)
  const utilityFoot =
    utility != null
      ? `Level-ups ${fmtRivCompact(utility.levelUps)} · Emergency exits ${fmtRivCompact(utility.emergencyExits)}`
      : investor?.utilityPool != null
        ? `Level-ups ${fmtRivCompact(investor.utilityPool.levelUpsRiv ?? 0)} · Emergency exits ${fmtRivCompact(investor.utilityPool.emergencyExitsRiv ?? 0)}`
        : "Level-ups + emergency exits + chest flows"

  return (
    <div className="tk-live-inner">
      <div className="tk-live-head">
        <div>
          <span className="tk-live-kicker">// PROTOCOL_LIVE</span>
          <h3 className="tk-live-title">On-chain totals</h3>
          <p className="tk-live-sub">
            Same Convex subscription as the Rivyu DApp — pool rewards paid, stake TVL, utility pool, and burn totals (ignored
            accounts excluded from reward-pool aggregates when admin cache is warm).
          </p>
        </div>
        <LiveConvexMeta updatedAt={tractionUpdatedAt ?? global?.updatedAt} />
      </div>
      <div className="tk-live-grid">
        <div className="tk-live-cell">
          <span className="tk-live-label">TOTAL RIV MINED</span>
          <span className="tk-live-val">
            {loading ? "···" : <CountUp to={Math.round(totalMined)} dur={1200} fmt={fmtRivCompact} />}
            <span className="tk-live-unit">RIV</span>
          </span>
          <span className="tk-live-foot">
            {loading
              ? "—"
              : `Mining pool paid ${fmtRivCompact(miningPoolPaid)} + staking pool paid ${fmtRivCompact(stakingPoolPaid)} (active users)`}
          </span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">MINING POOL REWARDS PAID</span>
          <span className="tk-live-val">
            {loading ? "···" : <CountUp to={Math.round(miningPoolPaid)} dur={1200} fmt={fmtRivCompact} />}
            <span className="tk-live-unit">RIV</span>
          </span>
          <span className="tk-live-foot">
            {loading ? "—" : `~${poolPct.toFixed(1)}% of 300M mining pool cap distributed`}
          </span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">TOTAL RIV STAKED</span>
          <span className="tk-live-val cyan">
            {loading ? "···" : <CountUp to={Math.round(totalStaked)} dur={1200} fmt={fmtRivCompact} />}
            <span className="tk-live-unit">RIV</span>
          </span>
          <span className="tk-live-foot">
            {uniqueStakers != null
              ? `${uniqueStakers.toLocaleString("en-US")} unique staker wallets`
              : "Active stake TVL"}
          </span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">STAKING POOL REWARDS PAID</span>
          <span className="tk-live-val cyan">
            {loading ? "···" : <CountUp to={Math.round(stakingPoolPaid)} dur={1200} fmt={fmtRivCompact} />}
            <span className="tk-live-unit">RIV</span>
          </span>
          <span className="tk-live-foot">Paid to active (non-ignored) stakers</span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">UTILITY POOL</span>
          <span className="tk-live-val amber">
            {loading && utility === undefined && !investorReady ? (
              "···"
            ) : (
              <CountUp to={Math.round(utilityPoolTotal)} dur={1200} fmt={fmtRivCompact} />
            )}
            <span className="tk-live-unit">RIV</span>
          </span>
          <span className="tk-live-foot">{utilityFoot}</span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">UNIQUE STAKERS</span>
          <span className="tk-live-val cyan">
            {loading && uniqueStakers == null ? (
              "···"
            ) : uniqueStakers != null ? (
              <CountUp to={uniqueStakers} dur={1000} />
            ) : (
              "—"
            )}
          </span>
          <span className="tk-live-foot">Distinct wallets with active stake</span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">TOTAL RIV BURNED</span>
          <span className="tk-live-val amber">
            {loading ? "···" : <CountUp to={totalBurned} dur={1200} fmt={fmtRivBurn} />}
            <span className="tk-live-unit">RIV</span>
          </span>
          <span className="tk-live-foot">
            {loading
              ? "—"
              : eco?.discrepancyDetected
                ? "Ledger vs chain discrepancy"
                : eco?.syncing
                  ? "On-chain verify in progress"
                  : "Ecosystem metrics OK"}
          </span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">DAU (24H ROLLING)</span>
          <span className="tk-live-val">{loading && !investorReady ? "···" : <CountUp to={dau} dur={1000} />}</span>
          <span className="tk-live-foot">
            {investorReady
              ? `Today UTC ${investor.engagement.dauCalendarToday.toLocaleString("en-US")} · MAU ${investor.engagement.mau.toLocaleString("en-US")}`
              : `Ignored / blocked: ${loading ? "—" : ignored.toLocaleString()}`}
          </span>
        </div>
      </div>

      <div className="tk-live-community-head">
        <span className="tk-live-kicker">// TRACTION_ENGAGEMENT</span>
        <p className="tk-live-sub tk-live-sub--community">
          Investor metrics from Backend.
        </p>
      </div>
      {investorLoading ? (
        <p className="tk-live-placeholder" style={{ marginTop: 12 }}>
          Loading traction metrics…
        </p>
      ) : !investorReady ? (
        <p className="tk-live-placeholder" style={{ marginTop: 12 }}>
          Traction cache not ready — deploy <code className="tk-live-code">investorStats:getPublicInvestorStats</code> and run{" "}
          <code className="tk-live-code">npx convex run investorStats:refreshInvestorStatsCache</code> on the DApp backend (cron
          fills this every 30m).
        </p>
      ) : (
        <>
          <div className="tk-live-meta" style={{ marginBottom: 12 }}>
            <span>SNAPSHOT {investor.snapshotAt.slice(0, 19).replace("T", " ")}Z</span>
            <span>STICKINESS {fmtPctRatio(investor.engagement.stickinessDauOverMau)}</span>
          </div>
          <div className="tk-live-grid tk-live-traction-grid">
            <div className="tk-live-cell">
              <span className="tk-live-label">TOTAL USERS</span>
              <span className="tk-live-val">
                <CountUp to={investor.users.total} dur={1000} />
              </span>
              <span className="tk-live-foot">
                Wallet {investor.users.walletConnected.toLocaleString("en-US")} (
                {fmtPctRatio(investor.users.walletConnectedPct, 0)}) · full operators only
              </span>
            </div>
            <div className="tk-live-cell">
              <span className="tk-live-label">GUEST USERS</span>
              <span className="tk-live-val">
                <CountUp to={investor.users.guests} dur={1000} />
              </span>
              <span className="tk-live-foot">
                Mining off-chain, pre-upgrade · excluded from total users
              </span>
            </div>
            <div className="tk-live-cell">
              <span className="tk-live-label">WAU · MAU</span>
              <span className="tk-live-val cyan" style={{ fontSize: "clamp(22px, 3.5vw, 34px)" }}>
                <CountUp to={investor.engagement.wau} dur={1000} />
                <span className="tk-live-unit"> / </span>
                <CountUp to={investor.engagement.mau} dur={1000} />
              </span>
              <span className="tk-live-foot">
                DAU/MAU {fmtPctRatio(investor.engagement.stickinessDauOverMau)} · Wallet DAU{" "}
                {investor.engagement.walletConnectedDau.toLocaleString("en-US")}
              </span>
            </div>
            <div className="tk-live-cell">
              <span className="tk-live-label">D7 RETENTION</span>
              <span className="tk-live-val amber">
                {fmtPctRatio(investor.retention.d7.pct)}
              </span>
              <span className="tk-live-foot">
                Cohort {investor.retention.d7.cohortSize.toLocaleString("en-US")} · retained{" "}
                {investor.retention.d7.retained.toLocaleString("en-US")} (active last 24h)
              </span>
            </div>
            <div className="tk-live-cell">
              <span className="tk-live-label">NEW USERS (7D)</span>
              <span className="tk-live-val">
                <CountUp to={investor.growth.newUsers7d} dur={1000} />
              </span>
              <span className="tk-live-foot">
                24h {investor.growth.newUsers24h.toLocaleString("en-US")} · 30d{" "}
                {investor.growth.newUsers30d.toLocaleString("en-US")}
              </span>
            </div>
            <div className="tk-live-cell">
              <span className="tk-live-label">ACTIVE MINERS</span>
              <span className="tk-live-val">
                <CountUp to={investor.mining.uniqueActiveMiners} dur={1000} />
              </span>
              <span className="tk-live-foot">
                Sessions {investor.mining.activeSessions.toLocaleString("en-US")} · ever mined{" "}
                {investor.mining.usersWhoEverMined.toLocaleString("en-US")}
              </span>
            </div>
            <div className="tk-live-cell">
              <span className="tk-live-label">REFERRERS · BURN</span>
              <span className="tk-live-val amber" style={{ fontSize: "clamp(22px, 3.5vw, 34px)" }}>
                <CountUp to={investor.referrals.uniqueReferrers} dur={1000} />
              </span>
              <span className="tk-live-foot">
                Chest burn {fmtRivCompact(investor.chests.totalRivBurnedOnChain)} RIV · opened{" "}
                {investor.chests.totalOpened.toLocaleString("en-US")} / {investor.chests.totalAwarded.toLocaleString("en-US")}
              </span>
            </div>
          </div>
        </>
      )}

      <div className="tk-live-community-head">
        <span className="tk-live-kicker">// COMMUNITY_PROGRAMS</span>
        <p className="tk-live-sub tk-live-sub--community">
          Idea Pad and bug bounty — same Convex reads as the DApp. Approved ideas count any submission with an approval
          timestamp (includes shipped). Bug bounty RIV paid out sums amounts credited after on-chain pool verification; awarded
          is total owed on resolved reports.
        </p>
      </div>
      <div className="tk-live-grid tk-live-community-grid">
        <div className="tk-live-cell">
          <span className="tk-live-label">IDEA PAD · SUBMITTED</span>
          <span className="tk-live-val cyan">
            {ideasLoading ? "···" : <CountUp to={ideas!.totalSubmitted} dur={1000} />}
          </span>
          <span className="tk-live-foot">
            {ideasLoading
              ? "—"
              : `Appr ${ideas!.approved.toLocaleString("en-US")} · Impl ${ideas!.implemented.toLocaleString("en-US")} · Rej ${ideas!.rejected.toLocaleString("en-US")} · Vote ${ideas!.inVoting.toLocaleString("en-US")}`}
          </span>
        </div>
        <div className="tk-live-cell">
          <span className="tk-live-label">BUG BOUNTY · REPORTS</span>
          <span className="tk-live-val amber">
            {bugsLoading ? "···" : <CountUp to={bugs!.totalReports} dur={1000} />}
          </span>
          <span className="tk-live-foot">
            {bugsLoading
              ? "—"
              : (
                  <>
                    Res {bugs!.resolved.toLocaleString("en-US")} · Conf {bugs!.confirmed.toLocaleString("en-US")} · Rej{" "}
                    {bugs!.rejected.toLocaleString("en-US")}
                    <br />
                    RIV {fmtRivCompact(bugs!.rivRewardsAwarded)} owed · {fmtRivCompact(bugs!.rivRewardsPaidOut)} paid
                    (verified)
                  </>
                )}
          </span>
        </div>
      </div>
    </div>
  )
}

export function TokenomicsLiveMetrics({ convexUrl }: { convexUrl: string }) {
  const url = convexUrl.trim()
  if (!url) {
    return (
      <div className="tk-live-wrap tk-live-wrap--muted">
        <p className="tk-live-placeholder">
          Set <code className="tk-live-code">NEXT_PUBLIC_CONVEX_URL</code> to the Rivyu Convex deployment to stream live protocol
          totals, investor traction (<code className="tk-live-code">investorStats</code>), Idea Pad, and bug bounty stats here.
        </p>
      </div>
    )
  }
  return (
    <div className="tk-live-wrap">
      <ConvexUrlProvider url={url}>
        <TokenomicsLiveMetricsInner />
      </ConvexUrlProvider>
    </div>
  )
}
